import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronRight,
  FileText,
  KeyRound,
  Monitor,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Store,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { type StoreRole } from "@/features/invites";
import { roleAtLeast, useRole } from "@/lib/auth";

export const Route = createFileRoute("/settings/")({
  component: SettingsPage,
});

function SettingsPage() {
  const role = useRole();

  return (
    <div className="space-y-6">
      <BackButton fallback={{ to: "/dashboard" }} />
      <div className="flex items-center gap-2">
        <SettingsIcon className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold text-foreground">Settings</h1>
      </div>

      {/* TNK-132 — /settings is now a pure tile grid grouped by purpose.
          Every section that used to live inline (store branding, listing
          thresholds, storefront, passkeys, members) now lives behind a
          tile so the landing reads as a workbench of related sub-pages. */}
      <SubPageGroups role={role} />
    </div>
  );
}

/**
 * TNK-132 — grid of sub-page tiles for the settings that have their own
 * route (eBay Integration, Listing Templates, Bundle Defaults). Grouped
 * by purpose so the page reads as a workbench of related groups rather
 * than a flat menu.
 */
interface SubPageTile {
  to: string;
  title: string;
  description: string;
  Icon: LucideIcon;
  min?: StoreRole;
}

interface SubPageGroup {
  title: string;
  description: string;
  tiles: SubPageTile[];
}

const SUBPAGE_GROUPS: SubPageGroup[] = [
  {
    title: "Store",
    description: "Branding, public storefront, and listing thresholds.",
    tiles: [
      {
        to: "/settings/store-options",
        title: "Store options",
        description:
          "Logo + watermark, storefront enable/URL/minimums, and the bulk-vs-single listing threshold.",
        Icon: Store,
        // GET+PUT /store/profile/me are both requireRole("manager") — Staff
        // could see this tile but the page could not even load for them.
        min: "manager",
      },
    ],
  },
  {
    title: "Integrations",
    description: "Connections to marketplaces and outside services.",
    tiles: [
      {
        to: "/settings/ebay",
        title: "eBay Integration",
        description:
          "Connect your eBay seller account, manage tokens, and sync sales.",
        Icon: Store,
        min: "manager",
      },
    ],
  },
  {
    title: "Templates & Defaults",
    description: "Reusable content and global generator behaviour.",
    tiles: [
      {
        to: "/settings/listing-templates",
        title: "Listing Templates",
        description:
          "Reusable description templates with placeholders that auto-fill from each card.",
        Icon: FileText,
        min: "manager",
      },
      {
        to: "/settings/bundle-defaults",
        title: "Bundle Defaults",
        description:
          "Global defaults for the bundle generator: strategies, profit vs speed, mystery-pack rarity mixes.",
        Icon: SlidersHorizontal,
        min: "manager",
      },
    ],
  },
  {
    title: "Account",
    description: "Sign-in security, display, and team access.",
    tiles: [
      {
        to: "/settings/account-security",
        title: "Account & Security",
        description:
          "Passkeys — phishing-resistant sign-in that works independently of eBay.",
        Icon: KeyRound,
      },
      {
        to: "/settings/display",
        title: "Display",
        description:
          "Theme: light, dark, or follow the OS. Per-operator; storefront keeps its own look.",
        Icon: Monitor,
      },
      {
        to: "/settings/user-management",
        title: "User Management",
        description:
          "Invite staff to share the store, assign owner / manager / staff roles, and remove access.",
        Icon: Users,
        min: "owner",
      },
    ],
  },
];

function SubPageGroups({ role }: { role: StoreRole | null }) {
  const groups = SUBPAGE_GROUPS.map((g) => ({
    ...g,
    tiles: g.tiles.filter((t) => !t.min || roleAtLeast(role, t.min)),
  })).filter((g) => g.tiles.length > 0);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.title} className="space-y-2">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </h2>
            <span className="text-[11px] text-muted-foreground/70">
              {group.description}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {group.tiles.map(({ to, title, description, Icon }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-start gap-3 rounded-md border border-border bg-card p-4 hover:border-primary/40 hover:bg-card/80"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="text-sm font-semibold text-foreground">
                    {title}
                  </div>
                  <div className="text-xs leading-snug text-muted-foreground">
                    {description}
                  </div>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
